import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex, Typo, Button, theme } from '../../../ui';
import useQuizSettingsEditor from '../hooks/useQuizSettingsEditor';

const PanelContainer = styled(Flex)`
  padding: 16px;
  height: 100%;
  overflow-y: auto;
`;

const Header = styled.div`
  padding-bottom: 8px;
  border-bottom: 1px solid ${theme.colors.NEU_200};
  margin-bottom: 4px;
  width: 100%;
`;

const ButtonsContainer = styled(Flex)`
  padding-top: 12px;
  margin-top: auto;
  border-top: 1px solid ${theme.colors.NEU_200};
`;

/**
 * QuizSettingsEditor - Panel component for editing quiz settings
 * Renders a panel with header, form, and action buttons
 *
 * @param {Object} props
 * @param {Object} props.quizSettings - Current quiz settings
 * @param {string} props.quizName - Fallback quiz name (from section/page)
 * @param {Function} props.onSave - Callback when settings are saved
 * @param {Function} props.onClose - Callback when panel closes (optional)
 */
const QuizSettingsEditor = ({ quizSettings, quizName, onSave, onClose }) => {
  const { t } = useTranslation();

  const { form, handleSave } = useQuizSettingsEditor({
    quizSettings,
    quizName,
    onSave,
    compact: true,
    inputSize: 'small',
    isEditing: true,
  });

  return (
    <PanelContainer direction='column' gap='12' align='flex-start'>
      <Header>
        <Typo.MediumText fontWeight='600'>{t('quiz.settings')}</Typo.MediumText>
      </Header>

      {form}

      <ButtonsContainer gap='8' width='100%' justify='flex-end'>
        {onClose && (
          <Button onClick={onClose} variant='ghost' size='small'>
            {t('globals.close')}
          </Button>
        )}
        <Button onClick={handleSave} size='small'>
          {t('globals.save')}
        </Button>
      </ButtonsContainer>
    </PanelContainer>
  );
};

export default QuizSettingsEditor;
